'use client';

import { useCallback, useState } from 'react';
import { useLocale } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { purchaseService } from '@/features/payment/api/purchase';
import { tokensService } from '@/features/payment/api/tokens';
import { passesService } from '@/features/payment/api/passes';
import { passCheckoutSkus } from '@/features/payment/lib/pass-sku';
import { pollUntilGranted } from '@/features/payment/lib/grant-poll';
import {
  isCheckoutAvailableForDescriptor,
  openCheckoutForDescriptor,
  toPaddleLocale,
} from '@/shared/lib/paddle';
import { useUser } from '@/features/auth/model/auth.selectors';
import { readGeoCountry } from '@/shared/lib/geo-country';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import type { CheckoutDescriptor } from '@/types/checkout';
import type { TokenWalletScope } from '@/features/payment/api/purchase';
import {
  AnalyticsEvent,
  trackEvent,
  PurchaseCheckoutOutcome,
  PurchaseProductKind,
} from '@/shared/lib/analytics';
import {
  trackEcommerceCheckoutOpened,
  trackEcommercePurchase,
} from '@/shared/lib/metrica-ecommerce';
import {
  closedWithoutSaleDetail,
  reportCheckoutOutcome,
} from '@/features/payment/lib/purchase-telemetry';

/**
 * Refresh every ownership/entitlement surface after a Pass or subscription
 * purchase. The webhook owns the grant; this only accelerates the local flip so
 * the storefront swaps the buy CTA for the owned state without a manual reload.
 * WS `user/updated` + the reconcile layers remain the authoritative backstop.
 */
function invalidateEntitlementSurfaces(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.current });
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.list });
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.usage });
  queryClient.invalidateQueries({ queryKey: cacheKeys.auth.user });
  queryClient.invalidateQueries({ queryKey: cacheKeys.profile });
  queryClient.invalidateQueries({ queryKey: cacheKeys.payments });
  // The Pass catalog (usePasses) carries per-user is_owned; drop it so the
  // purchased card flips to its owned state.
  queryClient.invalidateQueries({ queryKey: cacheKeys.passes.all });
  // The store catalog's pack cards carry `has_active_pack` / `is_additional` /
  // `is_owned` — a Pro Plan purchase is a global unlock that flips them on
  // every building, so refresh the whole catalog too.
  queryClient.invalidateQueries({ queryKey: cacheKeys.storeCatalog.all });
  // Pro Plan is a GLOBAL unlock: every building's `pack.is_unlimited` flips
  // server-side with no per-project purchase row. The in-building nav glyph
  // (crown vs token balance) reads the cached project's pack state, so without
  // this the user keeps seeing the bare token number instead of the crown
  // until something else happens to refetch the project.
  queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
}

/**
 * Wait for the Pass / Pro Plan grant, then refresh the entitlement surfaces.
 *
 * Invalidating at `checkout.completed` alone re-read the PRE-purchase state:
 * Paddle confirms in the browser before its webhook lands, so the refetch
 * cached "not owned" and every gate stayed closed until a WS `user/updated`
 * arrived, or forever if the socket was down. Same race the Kit and token
 * paths already poll through. The poll watches the purchased row in
 * `GET /v1/passes` (server-derived `is_owned` / `is_active`), bounded by the
 * shared grant-poll budget. The surfaces are invalidated once the budget ends
 * either way: on a grant it flips the UI, on a miss it is one more read after a
 * slow webhook before the WS / reconcile backstop takes over.
 */
async function refreshAfterEntitlementPurchase(
  queryClient: QueryClient,
  sku: string
): Promise<void> {
  await pollUntilGranted(async () => {
    const response = await passesService.getPasses();
    return response.data.some(
      (pass) =>
        passCheckoutSkus(pass).includes(sku) &&
        (pass.attributes.is_owned === true || pass.attributes.is_active === true)
    );
  });
  invalidateEntitlementSurfaces(queryClient);
}

/**
 * Refresh after a token-package purchase: the balance scalar plus the payment
 * history. The Paddle webhook credits the ledger; this only accelerates the
 * local flip (WS `user/updated` remains the authoritative backstop).
 *
 * The immediate invalidation usually LOSES the race with the webhook (Paddle
 * confirms in the browser before its server-to-server call lands), so it is
 * followed by a short poll until the credit actually shows up — otherwise the
 * refetch just re-reads the pre-purchase balance and the number sits stale
 * until the WS event arrives, or forever if the socket is down.
 */
function invalidateAfterTokenPurchase(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: cacheKeys.balance });
  queryClient.invalidateQueries({ queryKey: cacheKeys.payments });
  // The token-package catalog carries per-user pricing/state; refresh it so
  // the bought package reflects the new balance-driven copy without waiting
  // out the 5min staleTime.
  queryClient.invalidateQueries({ queryKey: cacheKeys.tokenPackages.all });

  void pollUntilTokensCredited(queryClient);
}

/**
 * Refresh after an agent-wallet top-up. Only the API keys screen shows that
 * balance, so the human balance/pack caches are deliberately left alone.
 */
function invalidateAfterAgentTokenPurchase(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['user', 'agent-keys'] });
  queryClient.invalidateQueries({ queryKey: cacheKeys.payments });
}

/**
 * Poll the balance until the webhook's credit lands, then push it into the
 * cache so every mounted balance readout (nav pill, tokens hero, paywall)
 * reflects the top-up. Fire-and-forget; safe past unmount (stable QueryClient).
 * Mirrors the Kit grant poll in {@link usePackCheckout}.
 */
async function pollUntilTokensCredited(queryClient: QueryClient): Promise<void> {
  const before = queryClient.getQueryData<number>(cacheKeys.balance) ?? 0;

  await pollUntilGranted(async () => {
    const response = await tokensService.getBalance();
    const balance = response.data.attributes.current_balance;
    if (balance <= before) {
      return false;
    }
    queryClient.setQueryData(cacheKeys.balance, balance);
    return true;
  });
}

export interface ProductCheckoutCallbacks {
  /** checkout.completed — the host flips to its success/owned state. */
  onPurchased?: () => void;
  /** checkout.closed (purchase OR cancel) — the host restores its resting state. */
  onClosed?: () => void;
}

/**
 * Account-level checkout (Tokens Pro Pass = `pass`; Pro Plan =
 * `subscription`; token packages = `token`). Generalizes
 * {@link usePackCheckout} for products that
 * are NOT building-bound: it mints the correlation intent, opens the checkout
 * via the server-provided `descriptor` (provider-agnostic — Paddle today), and
 * refreshes ownership on completion. The webhook remains the source of truth.
 *
 * `available` is false when the API shipped no `descriptor` (country not served
 * on a web rail) or the Paddle client isn't configured — the CTA then shows the
 * server `web_payment_notice` instead of a dead button.
 */
export function useProductCheckout({
  descriptor,
  kind,
  sku,
  walletScope = 'app',
  callbacks,
}: {
  descriptor: CheckoutDescriptor | null | undefined;
  kind: 'pass' | 'subscription' | 'token';
  sku: string;
  /**
   * Which wallet a token purchase lands in (docs/agent-api/ADR-002). `api` is
   * the agent wallet, bought from the API keys screen; it is carried in the
   * purchase intent, so the webhook credits the right ledger scope.
   */
  walletScope?: TokenWalletScope;
  callbacks?: ProductCheckoutCallbacks;
}) {
  const queryClient = useQueryClient();
  const user = useUser();
  const locale = useLocale();
  const [isLaunching, setIsLaunching] = useState(false);
  const onPurchased = callbacks?.onPurchased;
  const onClosed = callbacks?.onClosed;

  const available = isCheckoutAvailableForDescriptor(descriptor);

  // Map the checkout kind onto the shared cross-platform product vocabulary.
  // `subscription` is Pro Plan on the web rail — the same product Apple and
  // Android report as `pro_plan`, so it must report identically or the
  // cross-platform funnel splits one product into two.
  const productKind: PurchaseProductKind =
    kind === 'token'
      ? PurchaseProductKind.TOKENS
      : kind === 'subscription'
        ? PurchaseProductKind.PRO_PLAN
        : PurchaseProductKind.PASS;

  const startCheckout = useCallback(async () => {
    if (!descriptor) {
      return;
    }

    setIsLaunching(true);
    try {
      // Funnel: fire at the TAP, before anything can go wrong — parity with
      // Apple and Android, which both moved their start events here for the
      // same reason. Firing only after the overlay cleared would make "0 token
      // checkouts" unreadable: it could not distinguish "nobody pressed the
      // button" from "everybody did and every checkout died".
      if (kind === 'token') {
        trackEvent(AnalyticsEvent.TOKEN_PURCHASE_STARTED, { package_slug: sku });
      }
      trackEcommerceCheckoutOpened(sku, kind);

      // T-Bank mints the purchase intent server-side inside the
      // checkout-session call — the redirect leaves the page, so no intent,
      // no overlay listeners, and the return page + webhook own the rest.
      if (descriptor.provider === 'tbank') {
        await openCheckoutForDescriptor(descriptor, { kind });
        return;
      }

      const intent =
        kind === 'subscription'
          ? await purchaseService.createSubscriptionIntent(sku)
          : kind === 'token'
            ? await purchaseService.createTokenIntent(sku, walletScope)
            : await purchaseService.createPassIntent(sku);

      // Paddle fires `closed` for BOTH a completed purchase and an abandoned
      // one, so completion has to be latched to tell them apart. Without this
      // every successful checkout would also report itself as cancelled.
      let completed = false;

      await openCheckoutForDescriptor(descriptor, {
        kind,
        correlationToken: intent.correlation_token,
        // Prefill buyer email (always known — logged-in) + live geo country so
        // Paddle can skip its "Your details" step. Paddle still owns final tax.
        customerEmail: user?.email || undefined,
        countryCode: readGeoCountry(),
        locale: toPaddleLocale(locale),
        onCompleted: () => {
          completed = true;
          trackEcommercePurchase(sku, kind, intent.correlation_token);
          if (kind === 'token') {
            // The agent wallet is derived from the ledger and is read by the
            // API keys screen, not by the balance scalar — polling the human
            // balance for it would never see the credit land.
            if (walletScope === 'api') {
              invalidateAfterAgentTokenPurchase(queryClient);
            } else {
              invalidateAfterTokenPurchase(queryClient);
            }
          } else {
            void refreshAfterEntitlementPurchase(queryClient, sku);
          }
          onPurchased?.();
        },
        onClosed: (info) => {
          if (!completed) {
            reportCheckoutOutcome(
              PurchaseCheckoutOutcome.USER_CANCELLED,
              productKind,
              closedWithoutSaleDetail(info),
              intent.correlation_token,
              info.elapsedMs,
            );
          }
          onClosed?.();
        },
      });
    } catch (error) {
      // Surface the server-provided failure (API-driven message) — without
      // this a failed T-Bank session / intent mint dies as an unhandled
      // rejection and the buy button just "does nothing".
      if (error instanceof ApiClientError) {
        // The intent may not exist yet (this IS the mint failing), so the
        // report carries no token — GA4 still keeps the data point.
        reportCheckoutOutcome(
          PurchaseCheckoutOutcome.CLIENT_ERROR,
          productKind,
          `api_${error.status ?? 'error'}`,
          undefined,
        );
        toast.error(error.message);
        return;
      }
      reportCheckoutOutcome(
        PurchaseCheckoutOutcome.CLIENT_ERROR,
        productKind,
        'unexpected',
        undefined,
      );
      throw error;
    } finally {
      setIsLaunching(false);
    }
  }, [descriptor, kind, productKind, sku, queryClient, user?.email, locale, onPurchased, onClosed]);

  return { available, isLaunching, startCheckout };
}
